import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function LeadAuditHistory() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [recordTypeFilter, setRecordTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchLogs = useCallback(() => {
    if (!companyId || companyId === 'holding') return;
    setLoading(true);
    const params = new URLSearchParams({ page, per_page: 50 });
    if (search) params.append('search', search);
    if (actionFilter) params.append('action', actionFilter);
    if (recordTypeFilter) params.append('record_type', recordTypeFilter);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);

    fetch(`${API}/companies/${companyId}/leads/audit?${params}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        setLogs(Array.isArray(data.data) ? data.data : []);
        setPagination({ current_page: data.current_page, last_page: data.last_page, total: data.total });
      })
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [companyId, page, search, actionFilter, recordTypeFilter, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  if (!companyId || companyId === 'holding') return <div style={{ padding: 32 }}><h2>Select a company</h2></div>;

  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
  const actionColors = { created: '#22c55e', updated: '#3b82f6', deleted: '#ef4444', stage_changed: '#8b5cf6', status_changed: '#f59e0b', owner_changed: '#6366f1', archived: '#9ca3af', restored: '#10b981', won: '#22c55e', lost: '#ef4444', reopened: '#3b82f6' };

  return (
    <div style={{ padding: 32 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20 }}>Audit History</h2>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input placeholder="Search..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: 220 }} />
        <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: 170 }}>
          <option value="">All Actions</option>
          {['created', 'updated', 'deleted', 'stage_changed', 'status_changed', 'owner_changed', 'archived', 'restored', 'won', 'lost', 'reopened'].map(a => (
            <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <select value={recordTypeFilter} onChange={e => { setRecordTypeFilter(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: 170 }}>
          <option value="">All Record Types</option>
          {['lead', 'opportunity', 'activity', 'proposal', 'contact', 'closure', 'source'].map(rt => (
            <option key={rt} value={rt}>{rt.charAt(0).toUpperCase() + rt.slice(1)}</option>
          ))}
        </select>
        <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: 160 }} title="From date" />
        <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: 160 }} title="To date" />
      </div>

      {/* Results count */}
      {pagination.total !== undefined && (
        <div style={{ marginBottom: 12, fontSize: 13, color: '#6b7280' }}>{pagination.total} records found</div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,.08)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              {['Date/Time', 'User', 'Action', 'Record Type', 'Description', 'Changes'].map((h, i) => (
                <th key={i} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {initialLoading ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No audit logs found</td></tr>
            ) : logs.map(log => (
              <tr key={log.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                  {new Date(log.created_at).toLocaleString()}
                </td>
                <td style={{ padding: '10px 14px', fontWeight: 500 }}>{log.user?.name || 'System'}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: '#fff', background: actionColors[log.action] || '#6b7280' }}>
                    {log.action?.replace(/_/g, ' ')}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', textTransform: 'capitalize' }}>{log.record_type}</td>
                <td style={{ padding: '10px 14px', maxWidth: 300 }}>
                  <div style={{ fontSize: 13 }}>{log.description}</div>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  {log.old_values && (
                    <details style={{ fontSize: 12 }}>
                      <summary style={{ cursor: 'pointer', color: '#6366f1' }}>View changes</summary>
                      <div style={{ marginTop: 4, background: '#f9fafb', padding: 8, borderRadius: 4 }}>
                        {Object.keys(typeof log.old_values === 'string' ? JSON.parse(log.old_values) : (log.old_values || {})).map(k => {
                          const oldVals = typeof log.old_values === 'string' ? JSON.parse(log.old_values) : log.old_values;
                          const newVals = typeof log.new_values === 'string' ? JSON.parse(log.new_values) : (log.new_values || {});
                          return (
                            <div key={k} style={{ marginBottom: 4 }}>
                              <strong>{k}:</strong> <span style={{ color: '#ef4444', textDecoration: 'line-through' }}>{String(oldVals[k] ?? '')}</span> → <span style={{ color: '#22c55e' }}>{String(newVals[k] ?? '')}</span>
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
            style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}>← Prev</button>
          <span style={{ padding: '6px 12px', fontSize: 14, color: '#6b7280' }}>Page {page} of {pagination.last_page}</span>
          <button onClick={() => setPage(Math.min(pagination.last_page, page + 1))} disabled={page === pagination.last_page}
            style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: page === pagination.last_page ? 'default' : 'pointer', opacity: page === pagination.last_page ? 0.5 : 1 }}>Next →</button>
        </div>
      )}
    </div>
  );
}
